/**
 * PokerVault cart-page client.
 *
 * Scope: `/cart`. No-ops on every other URL; layout/theme.liquid also
 * only loads this asset on cart templates.
 *
 * Responsibilities:
 *
 *   1. Boost Upsell add-to-order
 *      Click on `[data-pv-upsell-add]` → POST `/cart/add.js` → flip
 *      `data-in-cart="true"` on the host + refresh cart state so any
 *      listening component re-renders.
 *
 *   2. Discount-code form
 *      Intercept the `[data-pv-discount-form]` submit, POST
 *      `/cart/update.js` with `{discount: "<code>"}`, then fire
 *      `cart:update` so the summary re-evaluates.
 *
 *   3. Pay Now loading state
 *      On form submit, flip the button's loading state. Actual checkout
 *      handoff is owned by `pokervault-checkout.js` (prefill flow).
 *
 * Vanilla ES5+ IIFE, no deps.
 */
(function () {
  'use strict';

  var SCOPE_PATH = '/cart';
  if (window.location.pathname !== SCOPE_PATH) {
    return;
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function publishCartUpdate(cartJson) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartJson || null,
            sourceId: 'pv-review',
            data: {
              source: 'pv-review',
              itemCount:
                cartJson && typeof cartJson.item_count === 'number'
                  ? cartJson.item_count
                  : undefined,
            },
          },
        })
      );
    } catch (_e) {
      // Non-fatal.
    }
  }

  function postCart(endpoint, body) {
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(
      function (res) {
        if (!res.ok) return null;
        return res.json().catch(function () { return null; });
      },
      function () { return null; }
    );
  }

  // ---------------------------------------------------------------------------
  // Boost Upsell
  // ---------------------------------------------------------------------------

  function bindBoostUpsell() {
    var host = document.querySelector('[data-pv-upsell]');
    if (!host) return;
    var btn = host.querySelector('[data-pv-upsell-add]');
    if (!btn) return;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      if (host.getAttribute('data-in-cart') === 'true') return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      host.setAttribute('data-loading', 'true');
      btn.disabled = true;

      postCart('/cart/add.js', {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      }).then(function (result) {
        host.removeAttribute('data-loading');
        if (!result) {
          btn.disabled = false;
          return;
        }
        host.setAttribute('data-in-cart', 'true');
        btn.setAttribute('aria-pressed', 'true');
        fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { publishCartUpdate(cart); })
          .catch(function () { publishCartUpdate(null); });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Pay Now loading state
  // ---------------------------------------------------------------------------

  function bindPayNowLoading() {
    var form = document.querySelector('[data-pv-pay-now-form]');
    if (!form) return;
    form.addEventListener('submit', function () {
      var btn = form.querySelector('[data-pv-pay-now-button]');
      if (btn) btn.setAttribute('data-loading', 'true');
    });
  }

  // ---------------------------------------------------------------------------
  // Discount code form
  // ---------------------------------------------------------------------------

  function bindDiscountForm() {
    var form = document.querySelector('[data-pv-discount-form]');
    if (!form) return;
    var input = form.querySelector('[data-pv-discount-input]');
    var apply = form.querySelector('[data-pv-discount-apply]');
    if (!input || !apply) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var code = (input.value || '').trim();
      if (!code) return;

      apply.disabled = true;
      var previousText = apply.textContent;
      apply.textContent = '…';

      postCart('/cart/update.js', { discount: code }).then(function (cart) {
        apply.disabled = false;
        apply.textContent = previousText;
        if (cart) {
          publishCartUpdate(cart);
          input.value = '';
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function boot() {
    bindBoostUpsell();
    bindDiscountForm();
    bindPayNowLoading();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
